import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import { FormattedMessage, useIntl } from 'react-intl'

import { formsMessages } from '@/lib/core/messages'
import { Button } from '@/lib/core/components/Button'
import { TextField } from '@/lib/core/components/TextField'

import { useAssetFormState } from '@/lib/features/Assets/components/AddAssetModal/store/useAssetFormState'
import {
  ErrorBox,
  FormActions,
  FormGrid,
} from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.styles'
import {
  FormType,
  VehicleBasicFormState,
} from '@/lib/features/Assets/components/AddAssetModal/interfaces'
import messages from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.messages'

import { setFormErrors } from '@/lib/utils/forms'

const schema = z.object({
  description: z
    .string({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
  make: z.string().nullable().optional(),
  vin: z.string().nullable().optional(),
})

export const VehicleBasicForm = () => {
  const { formatMessage } = useIntl()
  const setAssetFormType = useAssetFormState((v) => v.setType)
  const initialValues = useAssetFormState((v) => v.state)
  const errors = useAssetFormState((v) => v.errors)

  const patchAssetFormState = useAssetFormState((v) => v.updateState)
  const lockAssetFormType = useAssetFormState((v) => v.lockFormType)

  const formMethods = useForm<VehicleBasicFormState>({
    defaultValues: {
      description: initialValues?.description || null,
      make: initialValues?.make || null,
      vin: initialValues?.vin || null,
    },
    resolver: zodResolver(schema),
  })
  const { formState, handleSubmit, setError } = formMethods

  const onSubmit = (values: VehicleBasicFormState) => {
    patchAssetFormState(values)
    lockAssetFormType(FormType.VEHICLE_BASIC)
    setAssetFormType(FormType.VEHICLE_DETAIL)
  }

  useEffect(() => {
    if (errors) {
      setFormErrors(setError, errors)
    }
  }, [errors])

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormGrid>
          <TextField
            id="add-asset-form-description"
            label={formatMessage(messages.description)}
            displayInline
            name="description"
            type="text"
            required
          />
          <TextField
            id="add-asset-form-vin"
            label={formatMessage(messages.vin)}
            displayInline
            type="text"
            name="vin"
          />
          <TextField
            id="add-asset-form-make"
            label={formatMessage(messages.make)}
            displayInline
            type="text"
            name="make"
          />
        </FormGrid>
        {formState.isSubmitted && !formState.isValid && (
          <ErrorBox>
            <FormattedMessage {...messages.customErrorCommunicate} />
          </ErrorBox>
        )}
        <FormActions>
          <Button minWidth $fitToContent size="large" variant="secondary" type="submit">
            <FormattedMessage {...messages.next} />
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}
